import type { Video } from '../../../types/Video';
import { VideoSource } from '../../types/enums/video-source';
import {
  vimeoPatterns,
  youtubePatterns,
  loomPatterns,
  vidyardPatterns,
  wistiaPatterns,
} from '../constants/video-link.constants';

export function getVideoSource(videoUrl) {
  if (isYoutubeUrl(videoUrl)) {
    return VideoSource.Youtube;
  }
  if (isVimeoUrl(videoUrl)) {
    return VideoSource.Vimeo;
  }
  if (isLoomUrl(videoUrl)) {
    return VideoSource.Loom;
  }
  if (isVidyardUrl(videoUrl)) {
    return VideoSource.Vidyard;
  }
  if (isWistiaUrl(videoUrl)) {
    return VideoSource.Wistia;
  }
  return VideoSource.Unknown;
}

export function isYoutubeUrl(url: string) {
  const matchingPattern = getYoutubeMatchingPattern(url);
  return matchingPattern > -1;
}

export function getYoutubeMatchingPattern(url: string): number {
  if (!/youtu\.?be/.test(url)) {
    return -1;
  }
  for (let i = 0; i < youtubePatterns.length; i++) {
    if (youtubePatterns[i].test(url)) {
      return i;
    }
  }
  return -1;
}

export function isVimeoUrl(url: string) {
  const matchingPattern = getVimeoURLMatchingPattern(url);
  return matchingPattern > -1;
}

export function getVimeoURLMatchingPattern(url: string) {
  if (!/vimeo/.test(url)) {
    return -1;
  }
  for (let i = 0; i < vimeoPatterns.length; i++) {
    if (vimeoPatterns[i].test(url)) {
      return i;
    }
  }
  return -1;
}

export function isLoomUrl(url: string) {
  const matchingPattern = getLoomURLMatchingPattern(url);
  return matchingPattern > -1;
}

export function getLoomURLMatchingPattern(url: string) {
  if (!/loom/.test(url)) {
    return -1;
  }
  for (let i = 0; i < loomPatterns.length; i++) {
    if (loomPatterns[i].test(url)) {
      return i;
    }
  }
  return -1;
}

export function isVidyardUrl(url: string) {
  const matchingPattern: number = getVidyardURLMatchingPattern(url);
  return matchingPattern > -1;
}

export function isWistiaUrl(url: string) {
  const matchingPattern: number = getWistiaURLMatchingPattern(url);
  return matchingPattern > -1;
}

export function getVidyardURLMatchingPattern(url: string) {
  if (!/vidyard/.test(url)) {
    return -1;
  }
  for (let i = 0; i < vidyardPatterns.length; i++) {
    if (vidyardPatterns[i].test(url)) {
      return i;
    }
  }
  return -1;
}

export function getWistiaURLMatchingPattern(url: string) {
  if (!/wistia\.com/.test(url)) {
    return -1;
  }
  for (let i = 0; i < wistiaPatterns.length; i++) {
    if (wistiaPatterns[i].test(url)) {
      return i;
    }
  }
  return -1;
}

export function getLoomEmbedUrl(url: string) {
  const matchingPattern: number = getLoomURLMatchingPattern(url);
  if (matchingPattern === -1) return '';
  const match = loomPatterns[matchingPattern].exec(url);
  if (!match) return '';
  return `https://www.loom.com/embed/${match[3]}?hideEmbedTopBar=true&t=0`;
}

export function getVidyardEmbedUrl(url: string) {
  const matchingPattern: number = getVidyardURLMatchingPattern(url);
  if (matchingPattern === -1) return '';
  const match = vidyardPatterns[matchingPattern].exec(url);
  if (!match) return '';
  return `https://play.vidyard.com/${match[2]}`;
}

export function getWistiaEmbedUrl(url: string) {
  const matchingPattern: number = getWistiaURLMatchingPattern(url);
  if (matchingPattern === -1) return '';

  const match = wistiaPatterns[matchingPattern].exec(url);
  if (!match) return '';

  return `https://fast.wistia.com/embed/iframe/${match[2]}`;
}

export function checkIfVideoExists(videos: Video[], url: string) {
  if (videos.length === 0) {
    return false;
  }
  return !!videos.find((video: Video) => video.videoUrl === url);
}

export function isEmbed(url: string) {
  return isLoomUrl(url) || isVidyardUrl(url) || isWistiaUrl(url);
}
